import React, { useState, useRef, useEffect } from 'react';
import { sharedStyles as styles } from '../styles/alfredStyles';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import saveThreadToFirestore from '../utils/saveThreadToFirestore';
import { v4 as uuidv4 } from 'uuid';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore'; // ✅ Add this at top if not present
import { db } from '../firebase'; // ✅ Also ensure this is imported
import ReactMarkdown from 'react-markdown';

const chatContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginTop: '40px',
};

const stripMarkdown = (markdown) => {
  return markdown
    .replace(/^###\s?(.*)$/gm, '<strong>$1</strong>') // Convert h3 to bold
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove remaining bold markup
    .replace(/^\s*[-*+]\s+/gm, '- ') // Normalize bullets
    .replace(/^>\s?/gm, '') // Remove blockquotes
    .replace(/\r?\n\s*\r?\n/g, '\n\n') // Remove extra newlines
    .trim();
};

const chatBubbleStyle = {
  padding: '12px 16px',
  borderRadius: '16px',
  maxWidth: '96.5%',
  whiteSpace: 'pre-wrap',
  lineHeight: '1.5',
  fontSize: '15px',
};

const userBubble = {
  ...chatBubbleStyle,
  backgroundColor: '#e0f0ff',
  alignSelf: 'flex-end',
};

const alfredBubble = {
  ...chatBubbleStyle,
  backgroundColor: '#f0f0f0',
  alignSelf: 'flex-start',
};


const IndustryExpertise = ({
  existingChatHistory = [],
  existingThreadId = '',
  existingThreadName = '',
  existingFormData = {},
  industryIntelResult = '',
  scrollToOutputOnMount = true, // ✅ default is true
}) => {
  const [industry, setIndustry] = useState('');
  const [geography, setGeography] = useState('');
  const [size, setSize] = useState('');
  const [messageHistory, setMessageHistory] = useState([]);
  const [initialReport, setInitialReport] = useState(null);
  const [input, setInput] = useState('');
  const [chatStarted, setChatStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const chatBottomRef = useRef(null);
  const topRef = useRef(null);
  const resultRef = useRef(null);
  const [dots, setDots] = useState('');
  const [briefingName, setBriefingName] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [isEditingFull, setIsEditingFull] = useState(false);
  const [fullEditedText, setFullEditedText] = useState('');
  const [justSavedSection, setJustSavedSection] = useState('');
  const [justCopied, setJustCopied] = useState(false);
  const auth = getAuth();
  const [threadId, setThreadId] = useState('');
  const scrollRef = useRef(null);
  const hasScrolled = useRef(false);

  useEffect(() => {
    if (existingFormData?.industry) setIndustry(existingFormData.industry);
    if (existingFormData?.geography) setGeography(existingFormData.geography);
    if (existingFormData?.size) setSize(existingFormData.size);
    if (existingThreadId) setThreadId(existingThreadId);
    if (existingThreadName) setBriefingName(existingThreadName);

    if (existingChatHistory.length > 0) {
      const fullHistory = existingChatHistory.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
      }));
      setMessageHistory(fullHistory);
      setInitialReport(fullHistory.find(msg => msg.role === 'assistant') || null);
      setChatStarted(true);
    }

    // ✅ If this is a rehydrated thread with a saved report, use it
    if (typeof industryIntelResult === 'string' && industryIntelResult.trim() !== '') {
      setInitialReport({ role: 'assistant', content: industryIntelResult });

      // Only replace full chat history if it's missing
      if (existingChatHistory.length === 0) {
        setMessageHistory([{ role: 'assistant', content: industryIntelResult }]);
      }

      // Always treat as loaded (so it doesn't try to regenerate)
      setChatStarted(true);
    }
  }, []);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setDots(prev => (prev.length < 3 ? prev + '.' : ''));
      }, 500);
    } else {
      setDots('');
    }
    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    if (scrollToOutputOnMount && !hasScrolled.current && initialReport && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      hasScrolled.current = true;
    }
  }, [initialReport, scrollToOutputOnMount]);

  const startChat = async (e) => {
    e.preventDefault();
    if (chatStarted) return;
    const newThreadId = `industry-${new Date().toISOString().replace(/[:.]/g, '-')}-${uuidv4()}`;
    setThreadId(newThreadId);
    const firstMessage = `Preparing your analysis of the relevant information for ${size} ${industry} companies in ${geography}.`;
    const newHistory = [{ role: 'user', content: firstMessage }];
    setMessageHistory(newHistory);
    setChatStarted(true);
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/industry-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messageHistory: newHistory,
          context: { industry, geography, size }
        }),
      });
      const data = await response.json();
      const assistantMessage = { role: 'assistant', content: data.result };
      setInitialReport(assistantMessage);
      setMessageHistory([...newHistory, assistantMessage]);

      const stripTags = (str) =>
        str.replace(/<\/?[^>]+(>|$)/g, ''); // Strips all HTML tags
      
      const fullChat = [...newHistory, assistantMessage].map((msg) => ({
        sender: msg.role === 'user' ? 'user' : 'alfred',
        text: msg.content,
      }));

      const defaultNameParts = [];
      if (industry.trim()) defaultNameParts.push(industry.trim());
      if (geography.trim()) defaultNameParts.push(geography.trim());
      if (size.trim()) defaultNameParts.push(`Size ${size.trim()}`);
      const baseName = defaultNameParts.join(' – ') || 'General';
      const autoName = `Industry Research: ${baseName}`;

      const user = auth.currentUser;
      if (user) {
        await saveThreadToFirestore(user.uid, {
          id: newThreadId,
          name: briefingName || autoName,
          pinned: false,
          folders: [],
          type: 'industry',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          data: {
            industryIntelResult: assistantMessage.content, // ✅ One-time core output
            chat: fullChat, // ✅ Initial chat array (briefing request + briefing reply)
            industry,
            geography,
            size
          },
          metadata: {
            industry,
            geography,
            companySize: size
          }
        });
      }

    } catch (err) {
      const errorMessage = { role: 'assistant', content: 'Something went wrong.' };
      setInitialReport(errorMessage);
      setMessageHistory([...newHistory, errorMessage]);
    }

    setLoading(false);
  };

  const sendFollowUp = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    if (!threadId) return;

    const newHistory = [...messageHistory, { role: 'user', content: input }];
    setMessageHistory(newHistory);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/industry-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messageHistory: newHistory,
          context: { industry, geography, size }
        }),
      });
      const data = await response.json();
      setMessageHistory([...newHistory, { role: 'assistant', content: data.result }]);
      const stripTags = (str) =>
        str.replace(/<\/?[^>]+(>|$)/g, ''); // Strips all HTML tags
      const fullChat = [...newHistory, { role: 'assistant', content: data.result }].map((msg) => ({
        sender: msg.role === 'user' ? 'user' : 'alfred',
        text: msg.content,
      }));

      const defaultNameParts = [];
      if (industry.trim()) defaultNameParts.push(industry.trim());
      if (geography.trim()) defaultNameParts.push(geography.trim());
      if (size.trim()) defaultNameParts.push(`Size ${size.trim()}`);
      const baseName = defaultNameParts.join(' – ') || 'General';
      const autoName = `Industry Research: ${baseName}`;

      const user = auth.currentUser;
      const threadRef = doc(db, 'users', user.uid, 'threads', threadId);
      const threadSnap = await getDoc(threadRef);
      const wasPinned = threadSnap.exists() ? threadSnap.data().pinned : false;

      await saveThreadToFirestore(user.uid, {
        id: threadId,
        name: briefingName || autoName,
        pinned: wasPinned,
        type: 'industry', // ✅ This line is mandatory
        data: {
          chat: fullChat, // ✅ Append updated messages
          industry,
          geography,
          size,
          // 🛡️ Preserve original result
          industryIntelResult: threadSnap.exists() && threadSnap.data()?.data?.industryIntelResult
            ? threadSnap.data().data.industryIntelResult
            : '',
        },
        metadata: {
          industry,
          geography,
          companySize: size
        }
      });

    } catch (err) {
      setMessageHistory([...newHistory, { role: 'assistant', content: 'Something went wrong.' }]);
    }

    setLoading(false);
  };

  const parseMarkdownToSections = (markdown) => {
    const sections = {};
    let currentSection = '';
    let buffer = [];

    markdown.split('\n').forEach(line => {
      const headerMatch = line.match(/^#+\s(.+)/); // ✅ handles #, ##, ###, etc.

      if (headerMatch) {
        if (currentSection) {
          sections[currentSection] = buffer.join('\n').trim();
        }
        currentSection = headerMatch[1].trim();
        buffer = [];
      } else {
        buffer.push(line);
      }
    });

    if (currentSection) {
      sections[currentSection] = buffer.join('\n').trim();
    }

    return sections;
  };

  const handleExportToWord = () => {
    if (!initialReport?.content) return;

    const parsedSections = initialReport?.content
      ? parseMarkdownToSections(
          (initialReport?.content || '')
            .split('\n')
            .filter(line => !line.toLowerCase().includes('undefined'))
            .join('\n')
        )
      : {};

    const paragraphs = [
      new Paragraph({
        children: [new TextRun({ text: 'Industry Research Report', bold: true, size: 36 })],
        spacing: { after: 400 },
      }),
    ];

    Object.entries(parsedSections).forEach(([section, text]) => {
      paragraphs.push(
        new Paragraph({
          children: [new TextRun({ text: section, bold: true, size: 28, underline: {} })],
          spacing: { after: 200 },
        })
      );

      text.split('\n').forEach((line) => {
        if (line.trim() !== '') {
          paragraphs.push(
            new Paragraph({
              children: [new TextRun({ text: line.trim(), size: 24 })],
              spacing: { after: 100 },
            })
          );
        }
      });
    });

    const doc = new Document({ sections: [{ properties: {}, children: paragraphs }] });

    Packer.toBlob(doc).then((blob) => {
      const cleanDefault = (industry || 'industry').replace(/[^\w\s-]/g, '');
      const cleanCustom = (briefingName || '').replace(/[^\w\s-]/g, '');
      const finalName = cleanCustom || `alfred-industry-research-${cleanDefault}`;
      saveAs(blob, `${finalName}.docx`);
    });
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(
      (initialReport?.content || '')
        .split('\n')
        .filter(line => !line.toLowerCase().includes('undefined'))
        .join('\n')
    );
  };

  const handleSaveEdit = (section) => {
    const cleanedContent = (initialReport?.content || '')
      .split('\n')
      .filter(line => !line.toLowerCase().includes('undefined'))
      .join('\n');

    const updated = { ...parseMarkdownToSections(cleanedContent), [section]: editedText };

    const combined = Object.entries(updated)
      .map(([title, body]) => `### ${title}\n${body}`)
      .join('\n\n');
    
    setInitialReport({ ...initialReport, content: combined });
    setEditingSection(null);
    setJustSavedSection(section);

    setTimeout(() => {
      setJustSavedSection('');
    }, 800); // flicker duration
  };

  const flickerStyle = {
    animation: 'flicker 0.8s ease-in-out',
  };
  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '30px', borderBottom: '4px solid #FFD700', display: 'inline-block', paddingBottom: '10px' }}>
          Compile Industry Research
        </h2>

        <form onSubmit={startChat} style={{ width: '100%' }}>
          <div>
            <label style={styles.label}>Industry</label>
            <input
              style={styles.input}
              type="text"
              placeholder="e.g., Roofing"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              disabled={chatStarted}
            />
          </div>
          <div>
            <label style={styles.label}>Geography (optional)</label>
            <input
              style={styles.input}
              type="text"
              placeholder="e.g., Florida"
              value={geography}
              onChange={(e) => setGeography(e.target.value)}
              disabled={chatStarted}
            />
          </div>
          <div>
            <label style={styles.label}>Company Size</label>
            <input
              style={styles.input}
              type="text"
              placeholder="e.g., $2M"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              disabled={chatStarted}
            />
          </div>
          <button type="submit" style={styles.button} disabled={loading || chatStarted}>
            {loading ? `Alfred is Generating Your Briefing${dots} ✏️` : 'Generate Industry Briefing'}
          </button>
        </form>

        {chatStarted && (
          <>

            {initialReport && (
              <>
                <div ref={scrollRef} style={{ position: 'relative', top: '-80px' }} />
                <div style={styles.resultBox}>
                  <div>
                    <div style={{ marginBottom: '16px' }}></div>

                    {isEditingFull ? (
                      <>
                        <textarea
                          value={fullEditedText}
                          onChange={(e) => setFullEditedText(e.target.value)}
                          rows={16}
                          style={{
                            width: '95%',
                            padding: '16px',
                            fontSize: '16px',
                            fontFamily: `'Helvetica Neue', sans-serif`,
                            lineHeight: '1.6',
                            backgroundColor: '#ffffff',
                            borderRadius: '12px',
                            border: '1px solid #ccc',
                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)',
                            whiteSpace: 'pre-wrap'
                          }}
                        />
                        <button
                          onClick={() => {
                            setInitialReport({ ...initialReport, content: fullEditedText });
                            setIsEditingFull(false);
                            setJustSavedSection('full');
                            setTimeout(() => setJustSavedSection(''), 800);
                          }}
                          style={{ marginTop: '8px', ...styles.button }}
                        >
                          💾 Save
                        </button>
                      </>
                    ) : (
                      <div style={justSavedSection === 'full' ? flickerStyle : {}}>
                        <div
                          style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', fontSize: '16px' }}
                          dangerouslySetInnerHTML={{
                            __html: stripMarkdown(
                              (initialReport?.content || '')
                                .split('\n')
                                .filter(line => !line.toLowerCase().includes('undefined'))
                                .join('\n')
                            )
                          }}
                        />

                        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                          <button
                            onClick={() => {
                              setIsEditingFull(true);
                              setFullEditedText(
                                (initialReport?.content || '')
                                  .split('\n')
                                  .filter(line => !line.toLowerCase().includes('undefined'))
                                  .join('\n')
                              );
                            }}
                            style={{
                              backgroundColor: '#f6f6f6',
                              border: 'none',
                              borderRadius: '12px',
                              padding: '10px 16px',
                              fontWeight: '600',
                              fontSize: '15px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                            }}
                          >
                            <span role="img" aria-label="edit">📝</span> Edit
                          </button>

                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(
                                (initialReport?.content || '')
                                  .split('\n')
                                  .filter(line => !line.toLowerCase().includes('undefined'))
                                  .join('\n')
                              );
                              setJustCopied(true);
                              setTimeout(() => setJustCopied(false), 1000);
                            }}
                            style={{
                              backgroundColor: '#f6f6f6',
                              border: 'none',
                              borderRadius: '12px',
                              padding: '10px 16px',
                              fontWeight: '600',
                              fontSize: '15px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                              ...(justCopied ? flickerStyle : {}),
                            }}
                          >
                            {justCopied ? '✅ Copied!' : '📋 Copy'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: '20px' }}>
                    <label style={styles.label}>🏷️ Optional Industry Briefing Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Roofing Industry Briefing"
                      value={briefingName}
                      onChange={(e) => setBriefingName(e.target.value)}
                      style={{
                        width: '96.5%',
                        padding: '12px',
                        fontSize: '16px',
                        borderRadius: '10px',
                        border: '1px solid #ccc',
                        marginBottom: '15px'
                      }}
                    />
                  </div>

                  <button
                    onClick={handleExportToWord}
                    style={{ ...styles.button, marginTop: '0px', marginBottom: '8px' }}
                  >
                    Export Briefing to Word
                  </button>
                </div> {/* ✅ FIXED: This closes styles.resultBox correctly */}
              </>
            )}

            {messageHistory.length > 2 && (
              <div style={styles.chatContainer}>
                {messageHistory.slice(2).map((msg, idx) => (
                  <div
                    key={idx}
                    style={{
                      ...styles.chatBubble,
                      ...(msg.role === 'user' ? styles.userBubble : styles.alfredBubble)
                    }}
                  >
                    <strong>{msg.role === 'user' ? 'You' : 'Alfred'}:</strong><br />
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown
                        children={msg.content}
                        components={{
                          p: ({ children }) => <p style={{ marginBottom: '8px' }}>{children}</p>,
                          strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
                          li: ({ children }) => <li style={{ marginBottom: '4px' }}>{children}</li>,
                        }}
                      />
                    ) : (
                      <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', fontSize: '15px' }}>
                        {msg.content}
                      </div>
                    )}
                  </div>
                ))}
                <div ref={chatBottomRef} />
              </div>
            )}

            {initialReport && (
              <form onSubmit={sendFollowUp} style={{ width: '100%' }}>
                <div style={{ width: '100%', marginTop: '30px' }}>
                  {loading && (
                    <div style={{
                      ...alfredBubble,
                      fontStyle: 'italic',
                      marginBottom: '12px',
                      maxWidth: '14%',
                      color: '#333',
                    }}>
                      <strong>Alfred:</strong><br />
                      Alfred is typing{dots}
                    </div>
                  )}

                  <label style={{ ...styles.label, marginBottom: '8px' }}>
                    Ask Alfred a Follow-Up Question:
                  </label>

                  <textarea
                    style={{
                      ...styles.input,
                      fontFamily: "'Helvetica Neue', sans-serif",
                    }}
                    rows={3}
                    placeholder="e.g., How does this differ in winter?"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendFollowUp(e);
                      }
                    }}
                  />
                  <button style={styles.button} disabled={loading}>
                    Ask Alfred
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default IndustryExpertise;